
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Edit, Trash, Check, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useState } from "react";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";

interface IDType {
  id: string;
  document_name: string;
  country_code: string;
  description?: string;
  required?: boolean;
  created_at?: string;
  proves_citizenship?: boolean;
  proves_residency?: boolean;
}

interface IDTypesTableProps {
  idTypes: IDType[];
  onDelete?: (id: string) => Promise<void>;
  onEdit?: (idType: IDType) => void;
}

export function IDTypesTable({ idTypes, onDelete, onEdit }: IDTypesTableProps) {
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const handleDelete = async (id: string) => {
    if (!onDelete) return;
    
    try {
      setDeleteLoading(id);
      await onDelete(id);
      toast.success("ID type deleted successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to delete ID type");
    } finally {
      setDeleteLoading(null);
    }
  };

  const renderAttributeBadge = (value?: boolean) => {
    return value ? (
      <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">
        <Check className="h-3 w-3 mr-1" />
        Yes
      </Badge>
    ) : (
      <Badge variant="outline" className="bg-transparent">
        <X className="h-3 w-3 mr-1" />
        No
      </Badge>
    );
  };

  if (isMobile) {
    return (
      <div className="space-y-4">
        {idTypes.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            No ID types found. Add one to get started.
          </div>
        ) : (
          idTypes.map((idType) => (
            <div key={idType.id} className="border rounded-md p-4 space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">{idType.document_name}</h3>
                <div>
                  {idType.required ? (
                    <Badge>Required</Badge>
                  ) : (
                    <Badge variant="outline">Optional</Badge>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-muted-foreground">Country:</div>
                <div>{idType.country_code}</div>
                
                <div className="text-muted-foreground">Description:</div>
                <div>{idType.description || '—'}</div>
                
                <div className="text-muted-foreground">Proves Citizenship:</div>
                <div>{renderAttributeBadge(idType.proves_citizenship)}</div>
                
                <div className="text-muted-foreground">Proves Residency:</div>
                <div>{renderAttributeBadge(idType.proves_residency)}</div>
              </div>
              
              <div className="pt-2 flex justify-end gap-2">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => onEdit?.(idType)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-destructive"
                      disabled={deleteLoading === idType.id}
                    >
                      {deleteLoading === idType.id ? (
                        <span className="animate-spin">...</span>
                      ) : (
                        <Trash className="h-4 w-4" />
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete ID Type</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this ID type? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete(idType.id)}
                        className="bg-destructive hover:bg-destructive/90"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))
        )}
      </div>
    );
  }

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Document Name</TableHead>
            <TableHead>Country</TableHead>
            <TableHead>Required</TableHead>
            <TableHead>Proves Citizenship</TableHead>
            <TableHead>Proves Residency</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {idTypes.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground py-6">
                No ID types found. Add one to get started.
              </TableCell>
            </TableRow>
          ) : (
            idTypes.map((idType) => (
              <TableRow key={idType.id}>
                <TableCell className="font-medium">{idType.document_name}</TableCell>
                <TableCell>{idType.country_code}</TableCell>
                <TableCell>
                  {idType.required ? (
                    <Badge>Required</Badge>
                  ) : (
                    <Badge variant="outline">Optional</Badge>
                  )}
                </TableCell>
                <TableCell>{renderAttributeBadge(idType.proves_citizenship)}</TableCell>
                <TableCell>{renderAttributeBadge(idType.proves_residency)}</TableCell>
                <TableCell>{idType.description || '—'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => onEdit?.(idType)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-destructive"
                          disabled={deleteLoading === idType.id}
                        >
                          {deleteLoading === idType.id ? (
                            <span className="animate-spin">...</span>
                          ) : (
                            <Trash className="h-4 w-4" />
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete ID Type</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this ID type? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(idType.id)}
                            className="bg-destructive hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
